import { Fragment, useEffect, useRef, useState } from "react";
import { useContainerBreakpoint } from "@/hooks/ui/useContainerBreakpoint";
import type { LucideIcon } from "lucide-react";
import {
	Edit3,
	Loader2,
	MoreHorizontal,
	PanelLeftOpen,
	PanelRightOpen,
	Pencil,
	Play,
	Square,
	Wrench,
} from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@ncoder/ui";
import { Button } from "@/components/atoms/studio/ui/button";
import { RunElapsed } from "@/components/atoms/testing/RunElapsed";
import { RunStatusDot } from "@/components/atoms/testing/ActivityDot";
import type { HealStage, TestPlan } from "@/services/testing";
import type { PrimaryAction } from "@/utils/testing/primaryAction";
import { healStageLabel } from "./HealProposalCard";

/** A test name longer than this is truncated everywhere; the input enforces the same ceiling. */
const MAX_PLAN_NAME_LENGTH = 80;

/**
 * Header container stops. Measured from what the bar actually holds, not the viewport: the header
 * sits beside two panels whose widths change independently of the window.
 */
const HEADER_STOPS = { roomy: 520 } as const;

/** One row of the overflow menu. Built as data so the trigger can be hidden when the list is empty. */
interface SecondaryItem {
	key: string;
	icon: LucideIcon;
	label: string;
	onSelect?: () => void;
	disabled?: boolean;
	/** Draw a separator above this row. */
	separated?: boolean;
}

export interface TestingWorkspaceHeaderProps {
	selectedPlan: TestPlan | null;
	/** Title for a draft session that has no plan record yet. */
	draftTitle?: string | null;
	loading: boolean;

	/** Run state, scoped to the session on screen. */
	runningHere: boolean;
	healingHere: boolean;
	runningElsewhere: boolean;
	planningActive: boolean;
	runStartedAtMs: number | null;
	healStage?: HealStage | null;

	/** The ONE primary action, resolved by resolvePrimaryAction. */
	primaryAction: PrimaryAction;
	onRun: () => void;
	onStop: () => void;

	/** Secondary actions — collapsed into the overflow menu. */
	onEdit: () => void;
	onHeal: () => void;
	onRename: (id: string, name: string) => void;
	canSelfHeal: boolean;
	healDisabled: boolean;

	/** Panel reveal buttons, shown only while the corresponding panel is off-canvas. */
	onRevealSessions?: () => void;
	onRevealInspector?: () => void;
}

/**
 * The workspace header.
 *
 * Two problems it exists to fix. First, the old bar was a fixed-height `flex-nowrap` row that could
 * hold Stop + Propose fix + Edit + Approve & Run + Approve at once — roughly 460px of buttons — inside
 * an `overflow-hidden` parent, so at ≤1280px the PRIMARY action was the thing clipped off-screen.
 * Here exactly one primary button is rendered (chosen by `resolvePrimaryAction`), everything else
 * lives in an overflow menu, and the title is the only element allowed to consume slack.
 *
 * Second, "what is happening" was told by seven simultaneous indicators that could disagree. There is
 * now one status pill.
 */
export function TestingWorkspaceHeader({
	selectedPlan,
	draftTitle,
	loading,
	runningHere,
	healingHere,
	runningElsewhere,
	planningActive,
	runStartedAtMs,
	healStage,
	primaryAction,
	onRun,
	onStop,
	onEdit,
	onHeal,
	onRename,
	canSelfHeal,
	healDisabled,
	onRevealSessions,
	onRevealInspector,
}: TestingWorkspaceHeaderProps) {
	const { ref: headerRef, attrs: headerAttrs, width: headerWidth } =
		useContainerBreakpoint<HTMLElement>(HEADER_STOPS);
	const roomy = headerWidth === 0 || headerWidth >= HEADER_STOPS.roomy;
	const busy = runningHere || healingHere;

	const primaryHandler =
		primaryAction.kind === "stop" ? onStop : primaryAction.kind === "run" ? onRun : undefined;

	// Derive the item list, then show the trigger only if it is non-empty. Computing a `hasSecondary`
	// boolean separately from the items' own conditions is how you end up opening an empty menu.
	const secondaryItems: SecondaryItem[] = [];
	if (selectedPlan && !busy) {
		secondaryItems.push({ key: "edit", icon: Edit3, label: "Edit in chat", onSelect: onEdit });
		if (canSelfHeal) {
			secondaryItems.push(
				healDisabled
					? {
							key: "heal-off",
							icon: Wrench,
							label: "Fix proposals are off for this workspace",
							disabled: true,
							separated: true,
						}
					: {
							key: "heal",
							icon: Wrench,
							label: "Propose a fix for the failure",
							onSelect: onHeal,
							separated: true,
						},
			);
		}
	}

	return (
		<header
			ref={headerRef}
			{...headerAttrs}
			className="tst-header flex shrink-0 items-center gap-1.5 px-2 sm:gap-2 sm:px-3"
		>
			{onRevealSessions && (
				<button
					type="button"
					onClick={onRevealSessions}
					data-tst-reveal="sessions"
					className="tst-interactive shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
					title="Show sessions (⌘B)"
					aria-label="Show sessions"
				>
					<PanelLeftOpen className="h-4 w-4" />
				</button>
			)}

			{/* Only this cluster may shrink, so the actions on the right can never be pushed off. */}
			<div className="flex min-w-0 flex-1 items-center gap-2">
				<PlanTitle
					plan={selectedPlan}
					draftTitle={draftTitle}
					disabled={busy}
					onRename={onRename}
				/>
				{loading && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground/60" />}
				<RunStatusPill
					runningHere={runningHere}
					healingHere={healingHere}
					runningElsewhere={runningElsewhere}
					planningActive={planningActive}
					planStatus={selectedPlan?.status ?? null}
					runStartedAtMs={runStartedAtMs}
					healStage={healStage}
				/>
			</div>

			<div className="flex shrink-0 items-center gap-1.5">
				{secondaryItems.length > 0 && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<button
								type="button"
								className="tst-interactive rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
								title="More actions"
								aria-label="More actions"
							>
								<MoreHorizontal className="h-4 w-4" />
							</button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-56">
							{secondaryItems.map(({ key, icon: Icon, label, onSelect, disabled, separated }) => (
								<Fragment key={key}>
									{separated && <DropdownMenuSeparator />}
									<DropdownMenuItem
										onSelect={onSelect}
										disabled={disabled}
										className="gap-2 text-xs"
									>
										<Icon className="h-3.5 w-3.5" />
										{label}
									</DropdownMenuItem>
								</Fragment>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				)}

				<PrimaryActionButton action={primaryAction} onClick={primaryHandler} roomy={roomy} />

				{onRevealInspector && (
					<button
						type="button"
						onClick={onRevealInspector}
						data-tst-reveal="inspector"
						className="tst-interactive shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
						title="Show run details (⌘J)"
						aria-label="Show run details"
					>
						<PanelRightOpen className="h-4 w-4" />
					</button>
				)}
			</div>
		</header>
	);
}

/** Editable session title. The rename affordance is a visible pencil, not a bare click on the text. */
function PlanTitle({
	plan,
	draftTitle,
	disabled,
	onRename,
}: {
	plan: TestPlan | null;
	draftTitle?: string | null;
	disabled: boolean;
	onRename: (id: string, name: string) => void;
}) {
	const [renaming, setRenaming] = useState(false);
	const [value, setValue] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	// Switching session cancels an in-progress rename rather than applying it to the new test.
	useEffect(() => {
		setRenaming(false);
	}, [plan?.id]);

	useEffect(() => {
		if (renaming) inputRef.current?.select();
	}, [renaming]);

	function start() {
		if (!plan || disabled) return;
		setValue(plan.name);
		setRenaming(true);
	}

	function commit() {
		if (!plan) return setRenaming(false);
		const next = value.trim();
		if (next && next !== plan.name) onRename(plan.id, next);
		setRenaming(false);
	}

	if (renaming && plan) {
		return (
			<input
				ref={inputRef}
				value={value}
				onChange={(e) => setValue(e.target.value)}
				onBlur={commit}
				onKeyDown={(e) => {
					if (e.key === "Enter") commit();
					if (e.key === "Escape") setRenaming(false);
				}}
				maxLength={MAX_PLAN_NAME_LENGTH}
				aria-label="Test name"
				className="h-7 min-w-0 max-w-[320px] flex-1 rounded border border-primary/50 bg-card px-2 text-[15px] font-semibold text-foreground outline-none focus:ring-1 focus:ring-primary"
			/>
		);
	}

	return (
		<span className="group flex min-w-0 items-center gap-1">
			<span
				className={`tst-title truncate ${plan ? "" : "text-muted-foreground"}`}
				title={plan?.name ?? undefined}
			>
				{plan?.name ?? draftTitle ?? "New test"}
			</span>
			{plan && !disabled && (
				<button
					type="button"
					onClick={start}
					className="tst-interactive flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
					title="Rename this test"
					aria-label="Rename this test"
				>
					<Pencil className="h-3 w-3" />
				</button>
			)}
		</span>
	);
}

/**
 * One pill for "what is going on". Replaces the running badge + heal badge + planning dots + elapsed
 * clock that used to sit side by side and could contradict each other during a reconnect.
 *
 * Below `sm` only the dot and the clock survive — the label is the first thing to go, because it is
 * the part a user can infer from context.
 */
function RunStatusPill({
	runningHere,
	healingHere,
	runningElsewhere,
	planningActive,
	planStatus,
	runStartedAtMs,
	healStage,
}: {
	runningHere: boolean;
	healingHere: boolean;
	runningElsewhere: boolean;
	planningActive: boolean;
	planStatus: string | null;
	runStartedAtMs: number | null;
	healStage?: HealStage | null;
}) {
	const state = resolveStatusDisplay({
		runningHere,
		healingHere,
		runningElsewhere,
		planningActive,
		planStatus,
		healStage,
	});
	if (!state) return null;
	const showClock = (runningHere || healingHere) && runStartedAtMs != null;

	return (
		<span
			className="inline-flex min-w-0 shrink items-center gap-1.5 rounded-full bg-muted/50 px-2 py-0.5"
			title={state.title ?? state.label}
		>
			<RunStatusDot status={state.dot} />
			<span className={`hidden truncate text-[11px] font-medium sm:inline ${state.tone}`}>
				{state.label}
			</span>
			{showClock && <RunElapsed startedAt={runStartedAtMs!} reassure />}
		</span>
	);
}

interface StatusDisplay {
	dot: "ok" | "err" | "warn" | "busy" | "neutral";
	label: string;
	tone: string;
	title?: string;
}

/** Priority order: live work → work elsewhere → gate → last verdict. Exactly one wins. */
function resolveStatusDisplay({
	runningHere,
	healingHere,
	runningElsewhere,
	planningActive,
	planStatus,
	healStage,
}: {
	runningHere: boolean;
	healingHere: boolean;
	runningElsewhere: boolean;
	planningActive: boolean;
	planStatus: string | null;
	healStage?: HealStage | null;
}): StatusDisplay | null {
	if (healingHere) {
		return {
			dot: "busy",
			label: "Preparing fix",
			tone: "text-warning-strong",
			title: healStageLabel(healStage),
		};
	}
	if (runningHere) return { dot: "busy", label: "Running", tone: "text-primary" };
	if (planningActive) return { dot: "busy", label: "Planning", tone: "text-primary" };
	if (runningElsewhere) {
		return {
			dot: "busy",
			label: "Another test running",
			tone: "text-muted-foreground",
			title: "A run owned by a different session is in flight — open it from the session list",
		};
	}
	if (planStatus === "passed") return { dot: "ok", label: "Passed", tone: "text-success-strong" };
	if (planStatus === "failed") return { dot: "err", label: "Failed", tone: "text-destructive-strong" };
	// `approved` still occurs on plans created before the approval gate was removed; both mean the
	// same thing now — the plan is runnable.
	if (planStatus === "proposed" || planStatus === "approved") {
		return { dot: "neutral", label: "Ready to run", tone: "text-muted-foreground" };
	}
	return null;
}

function PrimaryActionButton({
	action,
	onClick,
	roomy,
}: {
	action: PrimaryAction;
	onClick?: () => void;
	/** False on a narrow header — use the short label so the session name keeps its room. */
	roomy: boolean;
}) {
	const icon =
		action.kind === "stop" ? (
			<Square className="mr-1 h-3.5 w-3.5" />
		) : action.busy ? (
			<Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
		) : (
			<Play className="mr-1 h-3.5 w-3.5 fill-current" />
		);

	return (
		<Button
			type="button"
			size="sm"
			variant={action.kind === "stop" ? "destructive" : "default"}
			className="h-8 shrink-0"
			onClick={onClick}
			disabled={action.disabled || !onClick}
			title={action.hint}
			aria-label={action.label}
		>
			{icon}
			<span className="whitespace-nowrap">
				{action.busy && action.busyLabel
					? action.busyLabel
					: roomy
						? action.label
						: action.shortLabel}
			</span>
		</Button>
	);
}
